import Client from "fhir-kit-client";
import { Organization, ValueSet } from "fhir/r5";

// Types
export type OrganizationTypeOption = {
  value: string;
  label: string;
};

type ValueSetConcept = {
  code?: string;
  display?: string;
  system?: string;
  concept?: ValueSetConcept[];
  contains?: ValueSetConcept[];
};

// Client
const fhirClient = new Client({
  baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
});

const VALUE_SET_URL = process.env.REACT_APP_VALUESET_ORGANIZATIONTYPE_URL ?? "";

// Helpers
function normalizeLabel(value?: string | null): string {
  return (value ?? "").trim();
}

function collectLeafConcepts(
  concepts: ValueSetConcept[] = [],
  inheritedSystem?: string
): OrganizationTypeOption[] {
  return concepts.flatMap((concept) => {
    const system = concept.system ?? inheritedSystem;
    const children = concept.concept ?? concept.contains ?? [];
    const value = system && concept.code ? `${system}|${concept.code.trim()}` : (concept.code ?? "").trim();

    if (children.length > 0) {
      return collectLeafConcepts(children, system);
    }

    if (!value) {
      return [];
    }

    return [
      {
        value,
        label: normalizeLabel(concept.display ?? concept.code),
      },
    ];
  });
}

function deduplicateOptions(options: OrganizationTypeOption[]): OrganizationTypeOption[] {
  const seen = new Map<string, OrganizationTypeOption>();

  options.forEach((option) => {
    if (!seen.has(option.value)) {
      seen.set(option.value, option);
    }
  });

  return Array.from(seen.values()).sort((left, right) =>
    left.label.localeCompare(right.label)
  );
}

// ValueSet loading
export async function loadOrganizationTypeOptions(): Promise<
  OrganizationTypeOption[]
> {
  if (!VALUE_SET_URL) {
    return [];
  }

  const response = await fhirClient.search({
    resourceType: "ValueSet",
    searchParams: {
      url: VALUE_SET_URL,
    },
  });

  if (response.resourceType !== "Bundle") {
    throw new Error("Unable to load ValueSet for organization types");
  }

  const valueSet = response.entry?.find(
    (entry: { resource?: { resourceType?: string } }) =>
      entry.resource?.resourceType === "ValueSet"
  )?.resource as ValueSet | undefined;

  if (!valueSet) {
    return [];
  }

  const expanded = valueSet.expansion?.contains?.length
    ? collectLeafConcepts(valueSet.expansion.contains)
    : [];

  const composed = valueSet.compose?.include?.length
    ? valueSet.compose.include.flatMap((entry) =>
        collectLeafConcepts(entry.concept ?? [], entry.system)
      )
    : [];

  return deduplicateOptions([...expanded, ...composed]);
}

// Label resolution
export function getOrganizationTypeLabel(
  organization: Organization,
  options: OrganizationTypeOption[]
): string {
  const firstCoding = organization.type
    ?.find((entry) => entry.coding?.length)
    ?.coding?.[0];

  if (!firstCoding) {
    return organization.type?.[0]?.text ?? "N/A";
  }

  const value =
    firstCoding.system && firstCoding.code
      ? `${firstCoding.system}|${firstCoding.code.trim()}`
      : (firstCoding.code ?? "").trim();
  const matchedOption = options.find((option) => option.value === value);

  return (
    matchedOption?.label ??
    firstCoding.display ??
    firstCoding.code ??
    organization.type?.[0]?.text ??
    "N/A"
  );
}
